import { useRef, useState } from "react";
import { uploadImageToIpfs } from "../utils/ipfs";

const ACCEPTED = "image/png,image/jpeg,image/gif,image/webp,image/svg+xml";

interface ImageUploadFieldProps {
  label: string;
  /** The IPFS hash/URL this field manages — same value that used to be a
   * plain paste-your-own-hash text input. */
  value: string;
  onChange: (hash: string) => void;
}

/**
 * Lets a creator pick or drag an image file, which gets pinned to IPFS via
 * Pinata through the pin-image Netlify function (the API key lives there
 * server-side, never in the browser) — instead of requiring them to pin it
 * themselves elsewhere first and paste the resulting hash. The "paste a
 * hash/URL instead" option is kept underneath, collapsed, for anyone who
 * already has one pinned or wants to point at an existing image.
 */
export function ImageUploadField({ label, value, onChange }: ImageUploadFieldProps) {
  const [status, setStatus] = useState<"idle" | "uploading" | "error">("idle");
  const [error, setError] = useState<string | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  async function handleFile(file: File | undefined | null) {
    if (!file) return;

    setStatus("uploading");
    setError(null);
    setPreviewUrl(URL.createObjectURL(file));

    try {
      const hash = await uploadImageToIpfs(file);
      onChange(hash);
      setStatus("idle");
    } catch (err) {
      setStatus("error");
      setError(err instanceof Error ? err.message : "Upload failed");
    }
  }

  return (
    <div className="image-upload">
      <label>
        {label}
        <div
          className="image-upload__dropzone"
          onClick={() => inputRef.current?.click()}
          onDragOver={(e) => e.preventDefault()}
          onDrop={(e) => {
            e.preventDefault();
            handleFile(e.dataTransfer.files[0]);
          }}
        >
          {previewUrl ? (
            <img src={previewUrl} alt="Preview" className="image-upload__preview" />
          ) : (
            <span>Click or drag an image here to upload</span>
          )}
        </div>
        <input
          ref={inputRef}
          type="file"
          accept={ACCEPTED}
          style={{ display: "none" }}
          onChange={(e) => handleFile(e.target.files?.[0])}
        />
      </label>

      {status === "uploading" && <p className="image-upload__status">Uploading to IPFS…</p>}
      {status === "error" && error && <p className="image-upload__status image-upload__status--error">{error}</p>}
      {value && status === "idle" && (
        <p className="image-upload__status">
          Pinned: <code>{value}</code>
        </p>
      )}

      <details className="image-upload__manual">
        <summary>Already have an IPFS hash or image URL? Paste it instead</summary>
        <input value={value} onChange={(e) => onChange(e.target.value)} placeholder="IPFS hash or https:// URL" />
      </details>
    </div>
  );
}
